// RfqCollabShell — the unified RFQ collaboration surface.
//
// One immersive shell, framed by the RFQ, with TWO tabs:
//   · Navigation — understand the package (Table / Tree views; Schematic later).
//     Clicking any node jumps to the Workspace focused on it.
//   · Workspace  — the real DrawingWorkspacePage, EMBEDDED (not rebuilt): canvas
//     + pins + collaboration, rooted at the RFQ's assembly so its own level nav
//     keeps working.
// Plus an always-available RFQ Response (quote / upload). Internal users get the
// shell directly; the supplier magic-link lands here in guest mode (later).

import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Paper, Stack, Typography, Button, Chip, Skeleton, Alert, alpha, useTheme,
  Tabs, Tab, IconButton, Tooltip, ToggleButtonGroup, ToggleButton,
  MenuItem, Menu, CircularProgress,
} from '@mui/material';
import {
  ArrowBackOutlined as BackIcon,
  AccountTreeOutlined as TreeIcon,
  TableRowsOutlined as TableIcon,
  SchemaOutlined as SchematicIcon,
  HubOutlined as WorkspaceIcon,
  DescriptionOutlined as Drawing2DIcon,
  ViewInArOutlined as Cad3DIcon,
  CleaningServicesOutlined as ProcessIcon,
  Inventory2Outlined as PartIcon,
  SendOutlined as ResponseIcon,
  ChevronRightOutlined as GoIcon,
  CheckCircleOutline as CheckIcon,
  BusinessOutlined as SupplierIcon,
  EventOutlined as DateIcon,
  ScheduleOutlined as DueIcon,
  PersonOutlineOutlined as RequesterIcon,
  Inventory2Outlined as QtyIcon,
  WidgetsOutlined as ScopeIcon,
  KeyboardArrowDownRounded as CaretIcon,
  VisibilityOutlined as ReviewIcon,
  RequestQuoteOutlined as QuoteActionIcon,
  DoNotDisturbAltOutlined as DeclineIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@ciea.ai/smart-auth-lib';
import ReactFlow, { Background, Controls, Position, type Node, type Edge } from 'reactflow';
import dagre from '@dagrejs/dagre';
import 'reactflow/dist/style.css';

import buyerAgentService from '../services/buyerAgentService';
import rfqPortalService from '../services/rfqPortalService';
import type { Workspace, WorkspaceItem } from '../services/workspaceService';
import DrawingWorkspacePage from './DrawingWorkspacePage';
import QuotePanel from '../components/buyer-agent/rfq-collab/QuotePanel';
import { setGuestLink } from '../services/guestLink';

const ACCENT = '#3d5a99';
const AMBER = '#a96a12';

// One rational type scale for the header card (4 steps, ~1.25 ratio) so sizes
// read as a hierarchy instead of arbitrary values.
const FS_TITLE = '1.125rem';   // 18px — RFQ number (primary)
const FS_IDENTITY = '0.875rem'; // 14px — part identity subtitle
const FS_VALUE = '0.8125rem';   // 13px — every field value / metric
const FS_LABEL = '0.6875rem';   // 11px — every uppercase micro-label
type ShellTab = 'navigation' | 'workspace' | 'response';
type NavView = 'table' | 'schematic';

/** Internal users mount at /rfq/:id/workspace; external suppliers mount at
 *  /portal/rfq/:token in guest mode (token-scoped data, no internal chrome). */
export default function RfqCollabShell({ guest = false }: { guest?: boolean } = {}) {
  const params = useParams<{ rfqId: string; token: string }>();
  const rfqId = params.rfqId ?? '';
  const token = params.token ?? '';
  const navigate = useNavigate();
  const theme = useTheme();

  // A supplier's page works through the link alone: the workspace's own calls follow it.
  if (guest) setGuestLink(token);
  useEffect(() => () => setGuestLink(undefined), []);

  const queryKey = guest ? ['portal-ws', token] : ['rfq-workspace', rfqId];
  const { data: ws, isLoading, isError, error } = useQuery<Workspace>({
    queryKey,
    queryFn: () => (guest ? rfqPortalService.getWorkspace(token) : buyerAgentService.getRfqWorkspace(rfqId)),
    enabled: guest ? !!token : !!rfqId,
  });

  const [tab, setTab] = useState<ShellTab>('navigation');
  const [navView, setNavView] = useState<NavView>('table');
  const [focusPartId, setFocusPartId] = useState<string | undefined>();
  const [focusDoc, setFocusDoc] = useState<{ documentNumber: string; kind?: 'drawing' | 'process' | 'cad'; rev?: string; fileName?: string } | undefined>();

  // The assembly the Workspace is rooted at (so its level nav covers the tree).
  const assemblyPartId = useMemo(
    () => ws?.items.find((i) => i.partClassification === 'ASSEMBLY')?.partId
      ?? ws?.items.find((i) => i.partId)?.partId,
    [ws],
  );
  useEffect(() => { if (!focusPartId && assemblyPartId) setFocusPartId(assemblyPartId); }, [assemblyPartId, focusPartId]);

  // Immersive Workspace tab — ask AppLayout to auto-collapse the main rail for
  // room; the Navigation tab restores it. Always restore on unmount.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('uvizion:sidebar', { detail: { collapse: tab === 'workspace' } }));
  }, [tab]);
  useEffect(() => () => {
    window.dispatchEvent(new CustomEvent('uvizion:sidebar', { detail: { collapse: false } }));
  }, []);

  // Navigation → Workspace: focus the node + switch tabs.
  const openInWorkspace = (item: WorkspaceItem) => {
    if (!item.partId || item.partClassification === 'RAW_MATERIAL') return;
    setFocusDoc(undefined);
    setFocusPartId(item.partId);
    setTab('workspace');
  };

  // Navigation → Workspace, landing on a specific document (drawing / process
  // spec / CAD) of the owning part. Works from both the table and the schematic.
  const openDocInWorkspace = (
    item: WorkspaceItem,
    doc: { documentNumber: string; kind?: 'drawing' | 'process' | 'cad'; rev?: string; fileName?: string },
  ) => {
    if (!item.partId) return;
    setFocusPartId(item.partId);
    setFocusDoc(doc);
    setTab('workspace');
  };

  if (isLoading) {
    return <Box sx={{ p: 4, maxWidth: 1100, mx: 'auto' }}><Skeleton variant="rounded" height={96} sx={{ mb: 2 }} /><Skeleton variant="rounded" height={480} /></Box>;
  }
  if (isError || !ws) {
    return <Alert severity="error" sx={{ m: 3 }}>Failed to load RFQ: {(error as any)?.message || 'unknown error'}</Alert>;
  }

  const anchors = ws.items.filter((i) => !i.informational && i.partClassification !== 'RAW_MATERIAL');
  const children = ws.items.filter((i) => i.informational && i.partClassification !== 'RAW_MATERIAL');
  const raw = ws.items.filter((i) => i.partClassification === 'RAW_MATERIAL');
  const quoteCount = ws.quotes?.length ?? 0;
  const viewer = (ws as any).viewer as { partnerId?: string; partnerName?: string } | undefined;
  const viewerName = viewer?.partnerName;
  // Guest entry (supplier magic-link) → lock the embedded workspace to this
  // supplier's role/identity. Internal entry → no persona, Engineer is default.
  const guestPersona = guest && viewer?.partnerId
    ? { id: viewer.partnerId, displayName: viewer.partnerName || 'Supplier' }
    : undefined;

  return (
    // Internal: in-flow panel inside AppLayout (negative margins cancel main's
    // padding). Guest: full-screen standalone surface (no AppLayout around it).
    <Box sx={guest
      ? { height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#fff' }
      : { height: 'calc(100vh - 80px)', mx: -3, mb: -3, display: 'flex', flexDirection: 'column', bgcolor: '#fff', border: `1px solid ${theme.palette.divider}` }}>
      {/* ── Single compact bar: identity (left) + tabs & controls (right) ── */}
      <Box sx={{ flexShrink: 0, px: 1.5, borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', alignItems: 'stretch', gap: 2, minHeight: 46 }}>
        {/* identity — internal: back + RFQ#. guest: brand + RFQ# + supplier. */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          {guest ? (
            <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: ACCENT, flexShrink: 0, mr: 0.5 }}>uVizion</Typography>
          ) : (
            <Tooltip title="Back to RFQ"><IconButton size="small" onClick={() => navigate(`/rfq/${rfqId}`)}><BackIcon fontSize="small" /></IconButton></Tooltip>
          )}
          <Typography sx={{ fontFamily: 'ui-monospace, monospace', fontWeight: 700, fontSize: '0.82rem', color: ACCENT, flexShrink: 0 }}>{ws.rfqNumber}</Typography>
          {!guest && ws.partnerName && (
            <Tooltip title="Lines and files as sent to this supplier">
              <Chip
                size="small"
                label={`${ws.partnerName} · Supplier Space`}
                variant="outlined"
                onClick={ws.partnerId ? () => navigate(`/partners/${ws.partnerId}#rfqs`) : undefined}
                sx={{ height: 20, fontSize: '0.62rem', fontWeight: 600, minWidth: 0 }}
              />
            </Tooltip>
          )}
          {guest && viewerName && <Chip size="small" label={viewerName} variant="outlined" sx={{ height: 20, fontSize: '0.62rem', fontWeight: 600, flexShrink: 0 }} />}
        </Stack>

        <Box sx={{ flex: 1 }} />

        {/* tabs — stretch full height so the underline sits on the bottom border.
            The Response tab is accent-colored (matches the old submit button) so
            it reads as the primary call to action. */}
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ minHeight: 46, alignSelf: 'stretch', '& .MuiTabs-indicator': tab === 'response' ? { backgroundColor: ACCENT } : undefined }}
        >
          <Tab value="navigation" icon={<TreeIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Package" sx={{ minHeight: 46, py: 0, textTransform: 'none' }} />
          <Tab value="workspace" icon={<WorkspaceIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Drawings" sx={{ minHeight: 46, py: 0, textTransform: 'none' }} />
          <Tab
            value="response"
            icon={<ResponseIcon sx={{ fontSize: 16 }} />}
            iconPosition="start"
            label={quoteCount > 0 ? `Quote (${quoteCount})` : guest ? 'Submit quote' : 'Quote'}
            sx={{ minHeight: 46, py: 0, textTransform: 'none', fontWeight: 700, color: ACCENT, '&.Mui-selected': { color: ACCENT } }}
          />
        </Tabs>

      </Box>

      {/* ── Decorated RFQ header card — hidden on the immersive Workspace tab ── */}
      {tab !== 'workspace' && (
        <RfqHeaderCard
          rfqId={rfqId}
          ws={ws}
          guest={guest}
          token={token}
          onQuote={() => setTab('response')}
          scope={{ assemblies: anchors.length, parts: children.length, raw: raw.length, docs: ws.packageManifest?.length ?? 0 }}
        />
      )}

      {/* ── Body ──────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
        {tab === 'navigation' ? (
          // The Table/Schematic toggle lives in the page (top-right), not the
          // header bar, so switching tabs doesn't shift the tab row around.
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', bgcolor: '#f7f9fc' }}>
            <Box sx={{ flexShrink: 0, display: 'flex', justifyContent: 'flex-end', px: 2, pt: 2, pb: 2 }}>
              <ToggleButtonGroup size="small" exclusive value={navView} onChange={(_, v) => v && setNavView(v)}>
                <ToggleButton value="table" sx={{ px: 1, py: 0.4, textTransform: 'none' }}><TableIcon sx={{ fontSize: 15, mr: 0.5 }} />Table</ToggleButton>
                <ToggleButton value="schematic" sx={{ px: 1, py: 0.4, textTransform: 'none' }}><SchematicIcon sx={{ fontSize: 15, mr: 0.5 }} />Schematic</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {/* Table and Schematic share the same start point below the toggle
                so flipping the view doesn't shift the content vertically. */}
            {navView === 'table' ? (
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 2, pb: 2 }}>
                {/* Internal users get clickable part deep-links; guests don't. */}
                <NavTable items={ws.items} onOpen={openInWorkspace} onOpenPart={guest ? undefined : (pid) => navigate(`/parts/${pid}`)} onOpenDoc={openDocInWorkspace} />
              </Box>
            ) : (
              // Schematic fills the remaining body (React Flow needs a bounded container).
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <NavSchematic items={ws.items} onOpen={openInWorkspace} onOpenDoc={openDocInWorkspace} />
              </Box>
            )}
          </Box>
        ) : tab === 'workspace' ? (
          // ONE workspace component for internal users AND guests — rooted at the
          // assembly so its level nav keeps working; focusPartId / focusDoc drive
          // what's shown.
          <DrawingWorkspacePage embedded partIdOverride={assemblyPartId} focusPartId={focusPartId} focusDoc={focusDoc} guestPersona={guestPersona} guestToken={guest ? token : undefined} partnerName={ws.partnerName} rfqScope={guest ? undefined : { rfqId, partnerId: ws.partnerId }} />
        ) : (
          <QuotePanel rfqId={rfqId} ws={ws} anchorPartId={assemblyPartId} guest={guest} token={token} />
        )}
      </Box>
    </Box>
  );
}

// ── Unified indented table ────────────────────────────────────────
// One view: BOM order preserved, hierarchy shown by indentation + a leading
// classification icon, columns for type / documents. Full width.
function typeMeta(item: WorkspaceItem): { label: string; color: string } {
  if (item.partClassification === 'ASSEMBLY') return { label: 'Assembly', color: ACCENT };
  if (item.partClassification === 'RAW_MATERIAL') return { label: 'Raw / stock', color: '#64748b' };
  return { label: 'Made part', color: '#0f766e' };
}

type DocRef = { documentNumber: string; kind?: 'drawing' | 'process' | 'cad'; rev?: string; fileName?: string };

function NavTable({ items, onOpen, onOpenPart, onOpenDoc }: { items: WorkspaceItem[]; onOpen: (i: WorkspaceItem) => void; onOpenPart?: (partId: string) => void; onOpenDoc?: (i: WorkspaceItem, doc: DocRef) => void }) {
  const theme = useTheme();
  const GRID = 'minmax(280px, 1.6fr) 120px minmax(160px, 1fr) 220px 36px';
  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: GRID, gap: 2, px: 2, py: 1, bgcolor: '#f1f5f9', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'text.secondary' }}>
        <span>Item</span><span>Type</span><span>Description</span><span>Documents</span><span />
      </Box>
      {items.map((item, idx) => {
        const depth = item.depth ?? 0;
        const isRaw = item.partClassification === 'RAW_MATERIAL';
        const isAssembly = item.partClassification === 'ASSEMBLY';
        const routable = !!item.partId && !isRaw;
        const tm = typeMeta(item);
        return (
          <Box key={`${item.partId}-${item.lineItemId ?? idx}`}
            onClick={() => routable && onOpen(item)}
            sx={{ display: 'grid', gridTemplateColumns: GRID, gap: 2, alignItems: 'center', px: 2, py: 1.1,
              borderTop: `1px solid ${theme.palette.divider}`, cursor: routable ? 'pointer' : 'default',
              '&:hover': routable ? { bgcolor: alpha(ACCENT, 0.04) } : undefined }}>
            {/* indented item cell */}
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0, pl: `${depth * 22}px` }}>
              {depth > 0 && <Box sx={{ color: 'text.disabled', fontFamily: 'monospace', fontSize: '0.8rem', lineHeight: 1 }}>└</Box>}
              {isAssembly ? <TreeIcon sx={{ fontSize: 16, color: 'text.secondary' }} /> : <PartIcon sx={{ fontSize: 16, color: isRaw ? 'text.disabled' : 'text.secondary' }} />}
              {/* Internal users: the PN deep-links to the Part page. stopPropagation
                  so it doesn't also trigger the row's open-in-workspace. */}
              <Typography
                onClick={item.partId && onOpenPart ? (e) => { e.stopPropagation(); onOpenPart(item.partId!); } : undefined}
                sx={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.8rem', fontWeight: isAssembly ? 700 : 600,
                  color: isRaw ? 'text.secondary' : 'text.primary',
                  cursor: item.partId && onOpenPart ? 'pointer' : 'inherit',
                  '&:hover': item.partId && onOpenPart ? { color: ACCENT, textDecoration: 'underline' } : undefined }}
                noWrap>{item.partNumber}</Typography>
              {item.revision && <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>rev {item.revision}</Typography>}
            </Stack>
            <Chip size="small" label={tm.label} variant="outlined" sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700, color: tm.color, borderColor: alpha(tm.color, 0.4), justifySelf: 'start' }} />
            <Typography variant="caption" color="text.secondary" noWrap>{item.description || '—'}</Typography>
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
              {item.drawing && <DocPip icon={<Drawing2DIcon sx={{ fontSize: 12 }} />} label="2D" onClick={onOpenDoc ? () => onOpenDoc(item, { documentNumber: item.drawing!.documentNumber, kind: 'drawing', rev: item.drawing!.revision, fileName: item.drawing!.fileName }) : undefined} />}
              {item.cad && <DocPip icon={<Cad3DIcon sx={{ fontSize: 12 }} />} label="3D" onClick={onOpenDoc ? () => onOpenDoc(item, { documentNumber: item.cad!.documentNumber ?? '', kind: 'cad', fileName: item.cad!.fileName }) : undefined} />}
              {(item.processSpecs ?? []).map((p) => <DocPip key={p.documentNumber} icon={<ProcessIcon sx={{ fontSize: 12 }} />} label={specNumber(p)} onClick={onOpenDoc ? () => onOpenDoc(item, { documentNumber: p.documentNumber, kind: 'process', rev: p.revision, fileName: p.fileName }) : undefined} />)}
            </Stack>
            {routable ? <GoIcon sx={{ color: 'text.disabled', fontSize: 18 }} /> : <Box />}
          </Box>
        );
      })}
    </Paper>
  );
}

// ── Schematic (deterministic node graph) ──────────────────────────
// Built purely from the BOM tree: nodes = items (+ one per unique process
// spec), edges = parent→child and item→spec. Layout via dagre — same input
// always yields the same diagram. No AI, no hand-authoring. Light theme to
// match the table; every part/assembly node navigates to the Workspace.
const NODE_W = 214;
const NODE_H = 88;

// Human document number — fileName (extension stripped) over the UUID id.
function specNumber(d: { documentNumber: string; fileName?: string }): string {
  const fromFile = d.fileName?.replace(/\.[a-z0-9]+$/i, '').trim();
  return (fromFile || d.documentNumber || 'spec').slice(0, 18);
}

// A package frame: a non-interactive dashed container that wraps a root's
// subtree, headed by the root PN. Registered as a custom node type so it has
// no handles and lets clicks pass through to the nodes inside it.
function FrameNode({ data }: { data: { label: string } }) {
  return (
    <div style={{ width: '100%', height: '100%', position: 'relative', border: '1.5px dashed #c3cedd', borderRadius: 16, background: 'rgba(61,90,153,0.025)' }}>
      <div style={{ position: 'absolute', top: 7, left: 14, fontFamily: 'ui-monospace, monospace', fontSize: '0.72rem', fontWeight: 700, color: '#64748b', letterSpacing: '0.02em' }}>{data.label}</div>
    </div>
  );
}
const SCHEMATIC_NODE_TYPES = { frame: FrameNode };

function buildGraph(items: WorkspaceItem[]): { nodes: Node[]; edges: Edge[]; graphBounds: { x0: number; y0: number; x1: number; y1: number } | null } {
  const nodes: Node[] = [];
  const edges: Edge[] = [];
  const byLine = new Map<string, WorkspaceItem>();
  items.forEach((it) => { if (it.lineItemId) byLine.set(it.lineItemId, it); });

  // Each top-level item is the root of a package subtree. Walk parent links to
  // find the root of any node so we can frame each package separately.
  const parentOf = new Map<string, string | null>();
  items.forEach((it) => {
    if (it.lineItemId) parentOf.set(it.lineItemId, it.parentLineItemId && byLine.has(it.parentLineItemId) ? it.parentLineItemId : null);
  });
  const rootOf = (id: string): string => {
    let cur = id;
    for (let i = 0; i < 64 && parentOf.get(cur); i++) cur = parentOf.get(cur)!;
    return cur;
  };
  const specRoot = new Map<string, string>(); // spec node id → its package root

  items.forEach((item) => {
    if (!item.lineItemId) return;
    const isAssembly = item.partClassification === 'ASSEMBLY';
    const isRaw = item.partClassification === 'RAW_MATERIAL';
    const routable = !!item.partId && !isRaw;
    // Plain wording: a 2D drawing and/or a 3D model, or bought stock.
    const docs = item.drawing && item.cad ? '2D + 3D' : item.drawing ? '2D' : item.cad ? '3D' : (isRaw ? 'material we buy' : '—');
    nodes.push({
      id: item.lineItemId,
      position: { x: 0, y: 0 },
      data: {
        label: (
          <Box sx={{ px: 1.25, width: '100%', textAlign: 'left' }}>
            <Typography sx={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.74rem', fontWeight: 700, color: isRaw ? '#64748b' : '#0f172a', lineHeight: 1.3 }} noWrap>{item.partNumber}{item.revision ? ` · rev ${item.revision}` : ''}</Typography>
            {item.description && <Typography sx={{ fontSize: '0.6rem', color: '#64748b', lineHeight: 1.25, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'normal' }}>{item.description}</Typography>}
            <Typography sx={{ fontSize: '0.58rem', color: isRaw ? '#94a3b8' : '#0f766e', mt: 0.25, fontWeight: 700 }}>{docs}</Typography>
          </Box>
        ),
      },
      style: {
        width: NODE_W, height: NODE_H, padding: 0, borderRadius: 10,
        display: 'flex', alignItems: 'center',
        border: isRaw ? '1.5px dashed #94a3b8' : `${isAssembly ? '2px' : '1.5px'} solid ${isAssembly ? ACCENT : '#cbd5e1'}`,
        background: isRaw ? '#f1f5f9' : '#ffffff',
        boxShadow: isAssembly ? '0 1px 6px rgba(61,90,153,0.18)' : '0 1px 3px rgba(15,23,42,0.06)',
        cursor: routable ? 'pointer' : 'default',
      },
      sourcePosition: Position.Bottom,
      targetPosition: Position.Top,
    });
    if (item.parentLineItemId && byLine.has(item.parentLineItemId)) {
      edges.push({ id: `e-${item.parentLineItemId}-${item.lineItemId}`, source: item.parentLineItemId, target: item.lineItemId, type: 'smoothstep', style: { stroke: '#94a3b8' } });
    }
  });

  // one node per unique process spec, linked to every item that references it
  const specSeen = new Set<string>();
  items.forEach((item) => {
    (item.processSpecs ?? []).forEach((spec) => {
      const id = `spec:${spec.documentNumber}`;
      if (!specSeen.has(id)) {
        specSeen.add(id);
        nodes.push({
          id, position: { x: 0, y: 0 },
          data: { label: (
            <Box sx={{ px: 1.25, width: '100%', textAlign: 'left' }}>
              <Typography sx={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.74rem', fontWeight: 700, color: AMBER, lineHeight: 1.3 }} noWrap>{specNumber(spec)}</Typography>
              <Typography sx={{ fontSize: '0.58rem', color: '#a98a55' }}>instruction document</Typography>
            </Box>
          ) },
          style: { width: NODE_W, height: NODE_H, padding: 0, borderRadius: 10, display: 'flex', alignItems: 'center', border: `1.5px dashed ${AMBER}`, background: '#fdf8ef', cursor: 'pointer' },
          sourcePosition: Position.Bottom, targetPosition: Position.Top,
        });
      }
      if (item.lineItemId) {
        if (!specRoot.has(id)) specRoot.set(id, rootOf(item.lineItemId));
        edges.push({ id: `es-${item.lineItemId}-${id}`, source: item.lineItemId, target: id, type: 'smoothstep', animated: false, style: { stroke: '#cbac6e', strokeDasharray: '4 3' } });
      }
    });
  });

  // dagre layout — deterministic
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: 'TB', nodesep: 34, ranksep: 58, marginx: 28, marginy: 28 });
  g.setDefaultEdgeLabel(() => ({}));
  nodes.forEach((n) => g.setNode(n.id, { width: NODE_W, height: NODE_H }));
  edges.forEach((e) => g.setEdge(e.source, e.target));
  dagre.layout(g);
  nodes.forEach((n) => {
    const p = g.node(n.id);
    n.position = { x: p.x - NODE_W / 2, y: p.y - NODE_H / 2 };
  });

  // One labeled dashed frame per package root — bounding box over its subtree
  // (parts + the spec docs they reference). Generalizes to several roots.
  const bounds = new Map<string, { x0: number; y0: number; x1: number; y1: number }>();
  for (const n of nodes) {
    const rid = n.id.startsWith('spec:') ? (specRoot.get(n.id) ?? n.id) : rootOf(n.id);
    const x0 = n.position.x, y0 = n.position.y, x1 = x0 + NODE_W, y1 = y0 + NODE_H;
    const b = bounds.get(rid);
    if (!b) bounds.set(rid, { x0, y0, x1, y1 });
    else { b.x0 = Math.min(b.x0, x0); b.y0 = Math.min(b.y0, y0); b.x1 = Math.max(b.x1, x1); b.y1 = Math.max(b.y1, y1); }
  }
  const PAD = 26, HEADER = 28;
  const frames: Node[] = [];
  for (const [rid, b] of bounds) {
    const root = byLine.get(rid);
    frames.push({
      id: `frame:${rid}`,
      type: 'frame',
      position: { x: b.x0 - PAD, y: b.y0 - PAD - HEADER },
      data: { label: root ? `${root.partNumber}${root.revision ? ` · rev ${root.revision}` : ''}` : 'Package' },
      style: { width: b.x1 - b.x0 + PAD * 2, height: b.y1 - b.y0 + PAD * 2 + HEADER, pointerEvents: 'none' },
      selectable: false, draggable: false, connectable: false, zIndex: 0,
    });
  }

  // Frames first so they paint behind the item nodes.
  const all = [...frames, ...nodes];
  // Overall bounds (incl. frames) so the schematic can be top-aligned to match
  // the table's first-row start, instead of fitView's vertical centering.
  let gx0 = Infinity, gy0 = Infinity, gx1 = -Infinity, gy1 = -Infinity;
  for (const n of all) {
    const w = (n.style?.width as number) ?? NODE_W;
    const h = (n.style?.height as number) ?? NODE_H;
    gx0 = Math.min(gx0, n.position.x); gy0 = Math.min(gy0, n.position.y);
    gx1 = Math.max(gx1, n.position.x + w); gy1 = Math.max(gy1, n.position.y + h);
  }
  const graphBounds = isFinite(gx0) ? { x0: gx0, y0: gy0, x1: gx1, y1: gy1 } : null;
  return { nodes: all, edges, graphBounds };
}

function NavSchematic({ items, onOpen, onOpenDoc }: { items: WorkspaceItem[]; onOpen: (i: WorkspaceItem) => void; onOpenDoc?: (i: WorkspaceItem, doc: DocRef) => void }) {
  const { nodes, edges, graphBounds } = useMemo(() => buildGraph(items), [items]);
  const wrapRef = useRef<HTMLDivElement>(null);
  // Top-align: zoom to fit width, then place the graph's top near the top of the
  // viewport (centered horizontally) so it starts at the same height as the table.
  const topAlign = useCallback((instance: any) => {
    const el = wrapRef.current;
    if (!el || !graphBounds) { instance.fitView({ padding: 0.15 }); return; }
    const W = el.clientWidth, H = el.clientHeight, pad = 28;
    const gW = graphBounds.x1 - graphBounds.x0, gH = graphBounds.y1 - graphBounds.y0;
    let zoom = Math.min((W - pad * 2) / gW, (H - pad * 2) / gH, 1.1);
    if (!isFinite(zoom) || zoom <= 0) zoom = 1;
    zoom = Math.max(zoom, 0.2);
    const x = (W - gW * zoom) / 2 - graphBounds.x0 * zoom;
    const y = pad - graphBounds.y0 * zoom;
    instance.setViewport({ x, y, zoom });
  }, [graphBounds]);
  const byLine = useMemo(() => {
    const m = new Map<string, WorkspaceItem>();
    items.forEach((it) => { if (it.lineItemId) m.set(it.lineItemId, it); });
    return m;
  }, [items]);
  // spec node id → the owning item + the doc, so clicking a spec opens it.
  const specRefs = useMemo(() => {
    const m = new Map<string, { item: WorkspaceItem; doc: DocRef }>();
    items.forEach((it) => (it.processSpecs ?? []).forEach((p) => {
      const id = `spec:${p.documentNumber}`;
      if (!m.has(id)) m.set(id, { item: it, doc: { documentNumber: p.documentNumber, kind: 'process', rev: p.revision, fileName: p.fileName } });
    }));
    return m;
  }, [items]);

  return (
    <Box ref={wrapRef} sx={{ width: '100%', height: '100%' }}>
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={SCHEMATIC_NODE_TYPES}
      onInit={topAlign}
      minZoom={0.2}
      proOptions={{ hideAttribution: true }}
      nodesDraggable={false}
      nodesConnectable={false}
      onNodeClick={(_, node) => {
        // Spec node → open that document; part/assembly → open the Workspace.
        if (node.id.startsWith('spec:')) {
          const ref = specRefs.get(node.id);
          if (ref && onOpenDoc) onOpenDoc(ref.item, ref.doc);
          return;
        }
        const item = byLine.get(node.id);
        if (item && item.partId && item.partClassification !== 'RAW_MATERIAL') onOpen(item);
      }}
      style={{ background: '#f7f9fc' }}
    >
      <Background color="#dbe3ee" gap={20} />
      <Controls showInteractive={false} />
    </ReactFlow>
    </Box>
  );
}

// ── Supplier status control ───────────────────────────────────────
// The supplier's self-service state switch. A modern pill-button + rich menu
// (icon · label · helper) instead of a bare, empty-looking <select>. The button
// reflects the current state's tone so it never reads as an unfilled form field.
const SUPPLIER_STATES: { value: string; label: string; help: string; icon: React.ReactNode; color: string }[] = [
  { value: 'UNDER_REVIEW', label: 'Reviewing', help: 'Looking over the package', icon: <ReviewIcon fontSize="small" />, color: ACCENT },
  { value: '__quote', label: 'Submit a quote', help: 'Open the response form', icon: <QuoteActionIcon fontSize="small" />, color: '#0f766e' },
  { value: 'REJECTED', label: 'Decline to quote', help: 'Not bidding on this RFQ', icon: <DeclineIcon fontSize="small" />, color: '#b91c1c' },
];

function SupplierStatusMenu({ current, pending, onPick }: { current?: string; pending: boolean; onPick: (v: string) => void }) {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  // QUOTE_RECEIVED no longer means "opened" (opening the portal only stamps
  // supplierViewedAt), so there is nothing to translate: it now means a quote
  // really landed, which is not one of the supplier's own switch positions.
  const active = SUPPLIER_STATES.find((s) => s.value === current);
  const tone = active?.color ?? ACCENT;
  return (
    <>
      <Button
        size="small"
        disabled={pending}
        onClick={(e) => setAnchor(e.currentTarget)}
        startIcon={pending ? <CircularProgress size={13} sx={{ color: tone }} /> : (active?.icon ?? <ReviewIcon fontSize="small" />)}
        endIcon={<CaretIcon sx={{ fontSize: 18 }} />}
        sx={{
          textTransform: 'none', fontWeight: 700, fontSize: FS_VALUE, lineHeight: 1.2,
          color: tone, borderRadius: 2, px: 1.25, py: 0.55,
          border: `1px solid ${alpha(tone, 0.4)}`, bgcolor: alpha(tone, 0.08),
          '& .MuiButton-endIcon': { ml: 0.25 },
          '&:hover': { bgcolor: alpha(tone, 0.15), borderColor: tone },
        }}
      >
        {active ? active.label : 'Update status'}
      </Button>
      <Menu
        anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { mt: 0.5, borderRadius: 2, minWidth: 248, boxShadow: '0 10px 30px rgba(0,0,0,0.18)' } } }}
      >
        <Typography sx={{ px: 1.75, pt: 1.25, pb: 0.5, fontSize: FS_LABEL, fontWeight: 800, letterSpacing: '0.06em', color: 'text.disabled' }}>
          YOUR STATUS
        </Typography>
        {SUPPLIER_STATES.map((s) => {
          const selected = s.value === active?.value;
          return (
            <MenuItem
              key={s.value} selected={selected}
              onClick={() => { setAnchor(null); onPick(s.value); }}
              sx={{ py: 1, px: 1.75, gap: 1.25, alignItems: 'flex-start' }}
            >
              <Box sx={{ color: s.color, mt: '2px', display: 'flex' }}>{s.icon}</Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: FS_VALUE, fontWeight: 600, color: 'text.primary' }}>{s.label}</Typography>
                <Typography sx={{ fontSize: FS_LABEL, color: 'text.secondary' }}>{s.help}</Typography>
              </Box>
              {selected && <CheckIcon sx={{ ml: 'auto', mt: '2px', fontSize: 16, color: s.color }} />}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}

// ── Decorated RFQ header card ─────────────────────────────────────
// Enterprise-grade summary banner: who/what/when at a glance. Pulls the full
// RFQ for buyer/dates/quantity that the workspace aggregate doesn't carry.
const REPLY_WINDOW_DAYS = 14; // standard reply window when no explicit due date is stored

function fmtDate(d?: string | Date | null): string {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function HeaderField({ icon, label, value, accent }: { icon: React.ReactNode; label: string; value: React.ReactNode; accent?: boolean }) {
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
      <Box sx={{ mt: '1px', color: accent ? AMBER : ACCENT, display: 'flex' }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: FS_LABEL, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'text.secondary' }}>{label}</Typography>
        <Typography sx={{ fontSize: FS_VALUE, fontWeight: 600, color: accent ? AMBER : 'text.primary', lineHeight: 1.35 }} noWrap>{value}</Typography>
      </Box>
    </Stack>
  );
}

function RfqHeaderCard({ rfqId, ws, scope, guest = false, token = '', onQuote }: { rfqId: string; ws: Workspace; scope: { assemblies: number; parts: number; raw: number; docs: number }; guest?: boolean; token?: string; onQuote?: () => void }) {
  const theme = useTheme();
  const auth = useAuth() as any;
  // Guests can't hit the authenticated RFQ endpoint; they rely on the workspace
  // payload alone (which carries status, partner, sentAt).
  const { data: rfq } = useQuery({ queryKey: ['rfq-detail', rfqId], queryFn: () => buyerAgentService.getRfq(rfqId), enabled: !guest && !!rfqId });

  const anchorItem = ws.items.find((i) => i.partClassification === 'ASSEMBLY') ?? ws.items.find((i) => !i.informational);
  const r = (guest ? ws : rfq) as any;
  const rfqDate = r?.sentAt ?? r?.requestedAt;
  const responded = r?.respondedAt;
  // Reply-by: use a stored due date if present, else a standard window from the RFQ date.
  const replyBy = r?.dueDate ?? r?.replyByDate ?? (rfqDate ? new Date(new Date(rfqDate).getTime() + REPLY_WINDOW_DAYS * 86400000) : null);
  const anchorLine = r?.lineItems?.find((li: any) => !li.informational) ?? r?.lineItems?.[0];
  const qty = anchorLine?.requestedQuantity ?? (anchorItem as any)?.requestedQuantity;
  const qtyUnit = anchorLine?.unit ?? (anchorItem as any)?.unit;

  // Requester name: prefer the stored name; else resolve the current user when
  // they are the requester (the common case); never show a raw UUID.
  const isUuid = (s?: string) => !!s && /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(s);
  const me = auth?.getCurrentUser?.();
  const myName = me ? (me.name || [me.firstName, me.lastName].filter(Boolean).join(' ') || me.displayName || me.fullName) : undefined;
  const buyer = r?.requestedByName
    || (r?.requestedBy && r.requestedBy === auth?.getCurrentUserId?.() ? (myName || auth?.getCurrentUserEmail?.()) : undefined)
    || (r?.requestedBy && !isUuid(r.requestedBy) ? r.requestedBy : undefined)
    || '—';

  // Guest: supplier-driven state changes via the portal.
  const qc = useQueryClient();
  const stateMutation = useMutation({
    mutationFn: (status: string) => rfqPortalService.setState(token, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portal-ws', token] }),
  });

  return (
    <Box sx={{ flexShrink: 0, px: 2, pt: 1.75, pb: 1.5, bgcolor: '#fff', borderBottom: `1px solid ${theme.palette.divider}` }}>
      <Paper
        variant="outlined"
        sx={{ borderRadius: 2.5, overflow: 'hidden', position: 'relative',
          borderColor: alpha(ACCENT, 0.25), bgcolor: '#fff' }}
      >
        {/* accent rail */}
        <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, bgcolor: ACCENT }} />
        <Box sx={{ display: 'flex', gap: 1.75, pl: 2.5, pr: 2, py: 1.6 }}>
          {/* thumbnail */}
          <Box sx={{ width: 48, height: 48, flexShrink: 0, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(ACCENT, 0.08), border: `1px solid ${alpha(ACCENT, 0.18)}` }}>
            <TreeIcon sx={{ fontSize: 25, color: ACCENT }} />
          </Box>

          {/* content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* title row */}
            <Stack direction="row" alignItems="center" spacing={1.1} sx={{ mb: 0.85, flexWrap: 'wrap', rowGap: 0.5 }}>
              <Typography sx={{ fontFamily: 'ui-monospace, monospace', fontWeight: 800, fontSize: FS_TITLE, color: ACCENT }}>{ws.rfqNumber}</Typography>
              {ws.status && <Chip size="small" label={ws.status} sx={{ height: 22, fontSize: FS_LABEL, fontWeight: 700, bgcolor: alpha(ACCENT, 0.12), color: ACCENT }} />}
              <Box sx={{ flex: 1 }} />
              {/* guest: supplier changes the RFQ state */}
              {guest && (
                <SupplierStatusMenu
                  current={ws.status}
                  pending={stateMutation.isPending}
                  onPick={(v) => { if (v === '__quote') { onQuote?.(); return; } stateMutation.mutate(v); }}
                />
              )}
              {/* reply-by — featured, like the part card's inventory metric */}
              <Stack direction="row" spacing={0.7} alignItems="center" sx={{ bgcolor: responded ? alpha('#0f766e', 0.08) : alpha(AMBER, 0.1), px: 1.1, py: 0.45, borderRadius: 1.5 }}>
                <DueIcon sx={{ fontSize: 17, color: responded ? '#0f766e' : AMBER }} />
                <Box>
                  <Typography sx={{ fontSize: FS_LABEL, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: responded ? '#0f766e' : AMBER, lineHeight: 1.2 }}>{responded ? 'RESPONDED' : 'REPLY BY'}</Typography>
                  <Typography sx={{ fontSize: FS_VALUE, fontWeight: 800, color: responded ? '#0f766e' : AMBER, lineHeight: 1.25 }}>{fmtDate(responded || replyBy)}</Typography>
                </Box>
              </Stack>
            </Stack>

            {/* part identity — number + rev only */}
            <Typography sx={{ fontFamily: 'ui-monospace, monospace', fontSize: FS_IDENTITY, color: 'text.primary', fontWeight: 700, mb: anchorItem?.description ? 0.9 : 1.1 }} noWrap>
              {anchorItem?.partNumber}{anchorItem?.revision ? `  ·  rev ${anchorItem.revision}` : ''}
            </Typography>

            {/* short description — labeled, on its own line (no inline dash) */}
            {anchorItem?.description && (
              <Box sx={{ mb: 1.1 }}>
                <Typography sx={{ fontSize: FS_LABEL, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'text.secondary', mb: 0.2 }}>
                  Short description
                </Typography>
                <Typography sx={{ fontSize: FS_VALUE, fontWeight: 500, color: 'text.primary', lineHeight: 1.4 }}>
                  {anchorItem.description}
                </Typography>
              </Box>
            )}

            {/* detail grid */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 1.5 }}>
              <HeaderField icon={<SupplierIcon sx={{ fontSize: 17 }} />} label="Supplier" value={ws.partnerName || '—'} />
              <HeaderField icon={<DateIcon sx={{ fontSize: 17 }} />} label="RFQ date" value={fmtDate(rfqDate)} />
              {!guest && <HeaderField icon={<RequesterIcon sx={{ fontSize: 17 }} />} label="Requested by" value={buyer || '—'} />}
              <HeaderField icon={<QtyIcon sx={{ fontSize: 17 }} />} label="Quantity" value={qty != null ? `${qty}${qtyUnit ? ` ${qtyUnit}` : ' each'}` : '—'} />
              <HeaderField icon={<ScopeIcon sx={{ fontSize: 17 }} />} label="Scope" value={`${scope.assemblies} assembly · ${scope.parts} parts · ${scope.raw} raw · ${scope.docs} docs`} />
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

function DocPip({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick?: () => void }) {
  return (
    <Chip size="small" icon={icon as any} label={label} variant="outlined"
      onClick={onClick}
      sx={{ height: 20, fontSize: '0.6rem', fontWeight: 600, color: 'text.secondary', cursor: onClick ? 'pointer' : 'default',
        '& .MuiChip-icon': { color: 'text.disabled', ml: '4px' },
        ...(onClick ? { '&:hover': { borderColor: ACCENT, color: ACCENT, bgcolor: alpha(ACCENT, 0.06) } } : {}) }} />
  );
}

// ── Response tab — structured quote OR upload the supplier's own quote doc ──
// Posts to the real `addQuote` endpoint. The two modes mirror how suppliers
// actually respond: fill the structured form, or attach their own quotation.
type ResponseMode = 'structured' | 'upload';
